import React, { useState } from 'react';
import PublicNav from '@/components/PublicNav';
import { Mail, MapPin, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Mock submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    console.log('Contact form submitted:', formData);
    toast.success('Message sent! We\'ll get back to you soon.');
    setFormData({ name: '', email: '', message: '' });
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#030712]">
      <PublicNav />
      
      <section className="pt-32 pb-20 px-6 md:px-12" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-7xl font-bold font-['Syne'] text-white mb-6" data-testid="contact-title">
            Let's <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-purple-400">Talk</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto" data-testid="contact-description">
            Have a project in mind? We'd love to hear from you.
          </p>
        </div>
      </section>

      <section className="pb-32 px-6 md:px-12" data-testid="contact-content">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12">
          <div>
            <h2 className="text-3xl font-bold font-['Syne'] text-white mb-6">Get In Touch</h2>
            <p className="text-slate-400 mb-8 leading-relaxed">
              Ready to start your project? Fill out the form and we'll get back to you within 24 hours.
            </p>
            
            <div className="space-y-6">
              <div className="flex items-start space-x-4" data-testid="contact-email">
                <div className="w-12 h-12 rounded-xl bg-indigo-500/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="text-indigo-400" size={20} />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Email</h3>
                  <p className="text-slate-400">clients@dhhomenet.co.uk</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4" data-testid="contact-phone">
                <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="text-purple-400" size={20} />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Phone</h3>
                  <p className="text-slate-400">+44 (0) 1443 123456</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4" data-testid="contact-location">
                <div className="w-12 h-12 rounded-xl bg-pink-500/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="text-pink-400" size={20} />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Location</h3>
                  <p className="text-slate-400">Pontypridd, Wales</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-slate-900/50 border border-white/10 rounded-2xl p-8">
            <form onSubmit={handleSubmit} data-testid="contact-form">
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-slate-300 mb-2">Name</label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Your name"
                    required
                    data-testid="contact-name-input"
                    className="bg-slate-950 border-slate-800 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-white placeholder:text-slate-600"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">Email</label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="your@email.com"
                    required
                    data-testid="contact-email-input"
                    className="bg-slate-950 border-slate-800 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-white placeholder:text-slate-600"
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-slate-300 mb-2">Message</label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    placeholder="Tell us about your project..."
                    rows={6}
                    required
                    data-testid="contact-message-input"
                    className="bg-slate-950 border-slate-800 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-white placeholder:text-slate-600 resize-none"
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-btn"
                  className="w-full bg-white text-black hover:bg-slate-200 font-semibold py-3 rounded-full transition-all duration-300"
                >
                  {loading ? 'Sending...' : 'Send Message'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 py-12 px-6" data-testid="footer">
        <div className="max-w-7xl mx-auto text-center text-slate-500">
          <p>&copy; 2026 DH HOME NETWORKING AND WEBSITES. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
